import axios from "axios"
import {
  encodedSymbols,
  searchNavigationParams,
  truncateTextInMiddleConstants,
  urlPatterns,
} from "../constants/constants"
import {
  breadcrumbs,
  COLOUR,
  fhPatterns,
  filterConstants,
  itemsPerPage,
  links,
  ranges,
  searchDefaults,
  selectors,
  tabs,
} from "../constants/search-constants"
import { CampaignType, NavigationParam, Tab, TileProduct, TileSparePart } from "../types/global"
import { FhResponseData } from "../types/search"
import { getProductTileAttributes } from "./product-utils"
import { getPartialSearchQueryUri } from "./search-query"
import { stemSpecialCharacters } from "./search-term-utils"
import { getSparePartTileAttributes } from "./spare-parts-utils"

export const getItems = (response: FhResponseData): FhResponseData => {
  return getUniverse(response)?.items_section?.items?.item ?? []
}

export const getFacetmap = (response: FhResponseData): FhResponseData => {
  return getUniverse(response).facetmap.find((facet: FhResponseData) => facet.universe === searchDefaults.UNIVERSE)
}

export const getUniverse = (response: FhResponseData): FhResponseData => {
  return response.data.universes.universe.find((u: FhResponseData) => u.name === searchDefaults.UNIVERSE)
}

export const getNumberOfResults = (response: FhResponseData): number => {
  return getUniverse(response).breadcrumbs.nr_of_items_in_selection
}

/* Extract the the items based on the campaign type */
export const getCampaignItems = (
  apiData: FhResponseData,
  campaignType: CampaignType
): (TileProduct & TileSparePart)[] => {
  /* Get the items based on the campaign type */
  const campaign = apiData?.universes?.universe?.[1]?.themes?.[0]?.theme?.find(
    (cmpgn) => cmpgn?.custom_fields?.custom_field?.[0]?.value === campaignType
  )

  return (
    campaign?.items?.item?.map((item) =>
      item?.id?.includes(tabs.PRO.toLocaleLowerCase())
        ? getProductTileAttributes(item)
        : getSparePartTileAttributes(item)
    ) ?? []
  )
}

/* Extract the carousel items from the api data response
  and sort them based on the order of a semi-colon separated list of IDs */
export const getSortedItems = (items: Array<TileProduct & TileSparePart>, sortByArray: Array<string>) => {
  /* All elements in sortByArray formatted to lower case */
  const sortByArrayL = sortByArray.map((el) => el.toLocaleLowerCase())

  /* Sort the items from the response based on the order coming from the Product API or a cookie */
  return items.sort(
    (a, b) => sortByArrayL?.indexOf(a?.id?.toLocaleLowerCase()) - sortByArrayL?.indexOf(b?.id?.toLocaleLowerCase())
  )
}

// Used in Normal breadcrumbs and Category Breadcrumbs
export const isBreadcrumbBannedCategory = (non_ml: string) => {
  if (!non_ml) return false

  const numberOfUnderscores = non_ml.split("_").length - 1

  // if more than one '_' don't ban the category breadcrumb
  if (numberOfUnderscores > 1) return false

  const isCategoryBreadcrumb = non_ml.includes(searchDefaults.SYSTEM_FH) || non_ml.includes(searchDefaults.SERIE_FH)

  return isCategoryBreadcrumb && numberOfUnderscores === 1
}

// Check if the breadcrumb data is for root category
export const isRootCategoryBreadCrumb = (non_ml: string) => {
  if (!non_ml) return false

  const numberOfUnderscores = non_ml.split("_").length - 1

  const isCategoryBreadcrumb = non_ml.includes(searchDefaults.SYSTEM_FH) || non_ml.includes(searchDefaults.SERIE_FH)

  return isCategoryBreadcrumb && numberOfUnderscores === 1
}

export const getTrimmedUrlParams = (urlParams: string) => {
  //Normalize the query string so we can easily match patterns in it
  const decodedUrlParams = decodeURIComponent(urlParams)

  //Remove everything before the '/status_selector=Live/' parameter
  return decodedUrlParams.substring(decodedUrlParams.indexOf(fhPatterns.SEPARATOR) + fhPatterns.SEPARATOR.length)
}

export const findActiveCategoryRemoveUrl = (breadcrumbsData: FhResponseData, categoryValue: string) => {
  // if active use the url_params from the breadcrumb before the one with the current name to make it inactive
  let removeUrlParams: string | undefined
  breadcrumbsData.some((crumb: FhResponseData, index: number) => {
    if (crumb.name.non_ml === `{${categoryValue}}`) {
      removeUrlParams = breadcrumbsData[index - 1].url_params
      return true
    }
  })

  return removeUrlParams
}

export const getDrilledCategories = (breadcrumbsData: FhResponseData) => {
  const drilledCategories = breadcrumbsData.filter(
    (crumb: FhResponseData) =>
      crumb.name?.attribute_type === selectors.CATEGORIES && !isBreadcrumbBannedCategory(crumb.name.non_ml)
  )

  return drilledCategories
}

export const isFilterToBeRemoved = (
  filterData: FhResponseData,
  currentTabCount: number,
  showNoResultsView: boolean
) => {
  const isBasetypeSetOrList =
    filterData.basetype === filterConstants.SET || filterData.basetype === filterConstants.LIST
  const renderedFilterOptions = filterData.filtersection.filter(
    (filterOption: FhResponseData) => !isFilterOptionToBeRemoved(filterOption, isBasetypeSetOrList, showNoResultsView)
  )
  //If there is no selected option and all options have the same number of items as the currently selected tab, remove the facet filter from the interface
  const isRelevant = renderedFilterOptions.some(
    (filterOption: FhResponseData) => filterOption.selected || filterOption.nr !== currentTabCount
  )

  return !isRelevant
}

export const isFilterOptionToBeRemoved = (
  filterOption: FhResponseData,
  isBasetypeSetOrList: boolean,
  showNoResultsView: boolean
) => {
  return (
    (filterOption.nr === 0 && !showNoResultsView) ||
    (isBasetypeSetOrList && filterOption.link.name === filterOption.value.value)
  )
}

export const truncateTextInMiddle = (
  text: string,
  strLen: number = truncateTextInMiddleConstants.DEFAULT_LENGTH,
  separator: string = truncateTextInMiddleConstants.DEFAULT_SEPARATOR
) => {
  if (text.length <= strLen) return text

  const charsToShow = strLen - separator.length
  const frontChars = Math.ceil(charsToShow / 2)
  const backChars = Math.floor(charsToShow / 2)

  return text.substr(0, frontChars) + separator + text.substr(text.length - backChars)
}

export const getAllCheckboxFiltersDefaultSelectedStatus = (filtersection: FhResponseData): object => {
  return filtersection.reduce((acc: object, current: FhResponseData) => {
    // current.selected can be undefined
    acc[current.value.value] = current.selected || false
    return acc
  }, {})
}

export const getCheckboxFilterSeparator = (display_hint: FhResponseData) => {
  if (Array.isArray(display_hint) && display_hint.length) {
    if (display_hint[0] === "multiselect-or") return ";"
    if (display_hint[0] === "multiselect-and") return ","
  }
  return ""
}

export const getSelectedProductsCount = (renderedFiltersection: FhResponseData) => {
  const productsCount: number = renderedFiltersection.reduce((acc: number, current: FhResponseData) => {
    if (current.selected) acc += current.nr
    return acc
  }, 0)

  return productsCount
}

export const getIsRangeFilter = (crumb: FhResponseData) => {
  const { value_set } = crumb.range
  if (value_set?.length > 1) {
    const lowConstraintAndAggregation = value_set.find(
      (el: FhResponseData) => el.constraint === filterConstants.LOW && el.aggregation === filterConstants.AND
    )
    const highConstraintAndAggregation = value_set.find(
      (el: FhResponseData) => el.constraint === filterConstants.HIGH && el.aggregation === filterConstants.AND
    )

    if (lowConstraintAndAggregation && highConstraintAndAggregation) {
      return true
    }
  }

  return false
}

/* Reconstruct the category tree by the last selected category id */

export const addReconstructedCategoryTreeToUrlParams = (urlParamsLink: string, isSeriesNavigation: boolean) => {
  const UNDERSCORE = "_"
  const urlSections = urlParamsLink.split(encodedSymbols.SLASH)

  // Get all the params in the url which are related to the selection in the category tree
  const categoriesSections = urlSections.filter((section) =>
    section.includes(`${breadcrumbs.CATEGORIES}${encodedSymbols.LESS_THAN}`)
  )

  // Get the main categoy, it will always be the first one
  const mainCategory = categoriesSections[0]
  const mainCategoryDepth = mainCategory.split(UNDERSCORE).length

  // Get the last selected category, it will always be the last applied to the url category
  const deepestCategory = categoriesSections[categoriesSections.length - 1]
  const deepestCategorySections = deepestCategory.split(UNDERSCORE)
  const drilledDepth = deepestCategorySections.length - 1

  // Gather all the reconstructed category params in this array
  let reconstructedCategoriesSections = []

  /* If the depth of the last selected category is greater than the number of category params in the url
    then auto drilling has been performed by FH and we have to reconstruct the url for the category tree */
  if (categoriesSections.length < drilledDepth) {
    // Apply the deepest selected category to the start of the array
    reconstructedCategoriesSections.push(deepestCategory)

    // Start reconstructing the tree by removing category IDs from the deepest level category
    while (deepestCategorySections.length > mainCategoryDepth) {
      // Remove a category ID
      deepestCategorySections.pop()

      // If on Series navigation don't add the main series category
      if (isSeriesNavigation && deepestCategorySections.length === mainCategoryDepth + 1) continue

      // Apply the next level category id value to the array
      reconstructedCategoriesSections.push(
        `${deepestCategorySections.join(UNDERSCORE)}${encodedSymbols.RIGHT_CURLY_BRACKET}`
      )
    }

    // Finally, reverse the order of the array so it starts from the main category
    reconstructedCategoriesSections.reverse()
  } else {
    // If no auto drilling has been performed use the original categories param from the url
    reconstructedCategoriesSections = [...categoriesSections]
  }

  /* Remove the main category from the reconstructed ones because
    we want to keep it in its original place in the url otherwise it's not selected correctly by FH in some cases */
  reconstructedCategoriesSections.shift()

  // Remove the categories we have reconstructed from the original url so no duplication occurs
  const filteredOutCategoriesSections = urlSections.filter(
    (section) => !reconstructedCategoriesSections.includes(section)
  )

  // Add the reconstructed category tree params to the url
  const reconstructedUrlSections = [...filteredOutCategoriesSections, ...reconstructedCategoriesSections]

  // Reconstruct the url from the gathered elements
  return reconstructedUrlSections.join(encodedSymbols.SLASH)
}

/* Get the remove url-params for each facet filter
  split it on '/' to get different url sections in array
  and then get only the parts which are the same in all urls
  which effectively filters out the applied filter values */

export const getRemoveAllLink = (breadcrumbFilters: FhResponseData) => {
  const filtersRemoveUrlsSections: Array<string[]> = breadcrumbFilters.reduce(
    (allFiltersRemoveUrls: Array<string[]>, filter: FhResponseData) => {
      const filterRemoveUrlElements: string[] = filter.link
        .find((l: FhResponseData) => l.type === links.REMOVE)
        .url_params.split(encodedSymbols.SLASH)
      allFiltersRemoveUrls.push(filterRemoveUrlElements)
      return allFiltersRemoveUrls
    },
    []
  )

  /* Remove unique parts of the urls so we are left only with what is
    the same in all remove links which is the remove link we are looking for */
  const removeAllFiltersUrlSections: Array<string> = filtersRemoveUrlsSections.reduce(
    (urlElements: Array<string>, filterRemoveUrl: Array<string>, index: number) => {
      // Initialize accumulator with the first url
      if (index === 0) return filterRemoveUrl

      /* Return only duplicate elements, filter out uniques
      this filters out the facet filters because
      there is always one facet missing from each remove url
      so at the end they are all filtered out */
      return urlElements.filter((el) => filterRemoveUrl.includes(el))
    },
    []
  )

  // Reconstruct the url from the remaining elements
  return removeAllFiltersUrlSections.join(encodedSymbols.SLASH)
}

/* Get attribute value from this structure in FH:
 { value: [{
     value: ''
 }] } */
const getAttributeValue = (attributeObj: FhResponseData): string => {
  return attributeObj?.value[0]?.value
}

/* Reduce FH array of attributes to a dictionary (e.g)
  articlenumber: ""
  name: ""
  etc... */
export const flattenAttributes = (attrArr: Array<FhResponseData>) => {
  return attrArr.reduce((values: object, attr: FhResponseData) => {
    values[attr.name] = getAttributeValue(attr)
    return values
  }, {})
}

export const checkImplicitRangesMatch = (
  attributes: Array<FhResponseData>,
  implicitRanges: Array<FhResponseData>,
  selectedColors: string | null
) => {
  if (!implicitRanges) {
    return false
  }

  // If colour is in "query ranges" and has more than one value we don't check for exact match.
  if (selectedColors && selectedColors.includes(";")) {
    return false
  }

  /* Filter attributes that match values in the implicit range
    one attribute can have multiple values in the implicit range
    logical operator for all the values is OR (||)
    the check here is if the product attribute value is contained in the
    implicit values array */
  const implicitRangesMatches = attributes.filter(({ name, value }) => {
    if (implicitRanges[name]) {
      const flattenedAttributeValues = value.map(({ non_ml }) => non_ml)
      return flattenedAttributeValues.some((fltnv: string) => implicitRanges[name].includes(fltnv))
    }
    return false
  })

  // If color is in the both "query ranges" and "implicit ranges" we don't check for exact match.
  if (implicitRangesMatches.findIndex((range) => range.name === COLOUR) >= 0 && selectedColors) {
    return false
  }

  // If color is in "query ranges" and "implicit ranges" has no values show exact match.
  if (implicitRangesMatches.length === 0 && selectedColors) {
    return true
  }

  // Check if at least one attribute is contained in the implicit ranges
  return implicitRangesMatches.length > 0
}

export const getReplacementInfo = (attributes: Array<FhResponseData>, replacementType: string): string => {
  let replacements = null
  const replacementAttribute = attributes.find(({ name }) => {
    return name === replacementType
  })
  if (replacementAttribute) {
    if (Array.isArray(replacementAttribute.value)) {
      replacements = replacementAttribute.value
        .filter(
          //replacements that are not available in the same locale
          //look like this: f0670fef_0917_46b2_98e5_2ab94342187f
          //so should be excluded and the rest should be concatenated with a ','
          ({ value }) => !(value.length === 36 && value.match(/_/g || []).length === 4)
        )
        .map(({ value }) => value)
        .join(", ")
    }
  }
  return replacements
}

/* Returns a promise which resolves in array holding all the partial results objects */
export const createPartials = async ({
  querySuggestions,
  searchTerm,
  searchServiceLocale,
  countryId,
  brand,
}: {
  querySuggestions: FhResponseData
  searchTerm: string
  searchServiceLocale: string
  countryId: string
  brand: string
}) => {
  //We need each iteration of the reduce to be asynchronous so we can react on it when it resolves
  //and only then add the resulting object to the array with partial results
  return querySuggestions.reduce(async (partialResultsPromise: Promise<unknown>, suggestion: string) => {
    const [queryParamValue, originalSearchTerm] = getMatchedAlternativeSearchTerm(searchTerm, suggestion)

    const productsSearchQueryUrl = getPartialSearchQueryUri({
      searchTerm: queryParamValue,
      searchServiceLocale,
      countryId,
      selectedTab: <Tab>tabs.PRO,
      brandValue: brand,
    })
    const sparePartsSearchQueryUrl = getPartialSearchQueryUri({
      searchTerm: queryParamValue,
      searchServiceLocale,
      countryId,
      selectedTab: <Tab>tabs.SPT,
      brandValue: brand,
    })

    //TODO (nan) Refactor to use SWR
    //Array containing the promises for getting products and spare parts
    const partialSearchPromises = [axios.get(productsSearchQueryUrl), axios.get(sparePartsSearchQueryUrl)]

    //Promise.all guarantees that it will resolve when all the passed promises resolve
    //and that we will receive the results in the same order as the passed array with promises
    await Promise.all(partialSearchPromises)
      .then(async (res: Array<FhResponseData>) => {
        const products: FhResponseData = getItems(<FhResponseData>res[0])
        const spareParts: FhResponseData = getItems(<FhResponseData>res[1])
        const totalNumberOfResults = getNumberOfResults(res[0]) + getNumberOfResults(res[1])

        //Wait for the single reduce iteration to resolve and only then push the resulting object in the array
        ;(await partialResultsPromise).push({
          items: composeItems(products, spareParts),
          originalSearchTerm: originalSearchTerm,
          partialSearchTerm: queryParamValue,
          numberOfResults: totalNumberOfResults,
          //needed for the analytics
          rid: {
            [tabs.PRO]: res[0]?.data.rid,
            [tabs.SPT]: res[1]?.data.rid,
          },
        })
      })
      .catch((err) => {
        throw new Error(err)
      })

    return partialResultsPromise
  }, Promise.resolve([]))
}

const composeItems = (products: FhResponseData, spareParts: FhResponseData): FhResponseData[] => {
  //Number of total tiles to show in the partial results
  const TOTAL_TILES = itemsPerPage.PARTIAL_RESULTS
  //Number of product tiles in the tiles composition
  const PRODUCT_TILES = 3

  const hasEnoughProducts = products.length >= PRODUCT_TILES
  const hasSpareParts = spareParts.length > 0

  let composedItems = []

  if (hasEnoughProducts) {
    if (hasSpareParts) {
      composedItems = [...products.slice(0, PRODUCT_TILES), spareParts[0]]
    } else {
      composedItems = [...products.slice(0, TOTAL_TILES)]
    }
  } else {
    composedItems = [...products, ...spareParts.slice(0, TOTAL_TILES - products.length)]
  }

  return composedItems
}

//Try to match the suggested partial search term to the words in the original one
//by the position of words in the alternative search term.
//The purpose is to circumvent the FH stemming algorithm for replacing characters
const getMatchedAlternativeSearchTerm = (searchTerm: string, suggestion: FhResponseData): [string, string] => {
  const stemmedSearchTerm: string = stemSpecialCharacters(searchTerm)
  const searchTermWords: Array<string> = stemmedSearchTerm.split(" ")
  const modifiedSearchTermWords: Array<string> = suggestion.original.split(" ")

  //If the searchTerm and the modified by FH searchTerm have different number of words
  //return the suggested by FH alternative search term
  if (searchTermWords.length !== modifiedSearchTermWords.length) {
    return [suggestion.value.value, suggestion.original]
  }

  const alternativeTermWords: Array<string> = suggestion.value.value.split(" ")
  const removedWordsIndexes = modifiedSearchTermWords.reduce((arr: Array<number>, word: string, i: number) => {
    if (!alternativeTermWords.includes(word)) arr.push(i)
    return arr
  }, [])
  const matchedAlternativeTermWords = searchTermWords.reduce((arr: Array<string>, word: string, i: number) => {
    if (!removedWordsIndexes.includes(i)) arr.push(word)
    return arr
  }, [])

  return [matchedAlternativeTermWords.join(" "), stemmedSearchTerm]
}

export const getNavigationSearchTerm = (
  transformedMeta: FhResponseData,
  queryParam: NavigationParam,
  noveltiesLabel: string
): string => {
  //If navigation referrer is Novelties, return localized label
  if (queryParam.name === searchNavigationParams.NEW) return noveltiesLabel

  //If navigation refererer is Systems or Series return the parent category name
  if (queryParam.name === searchNavigationParams.SYSTEMS || queryParam.name === searchNavigationParams.SERIES) {
    const drilledCategories = getDrilledCategories(transformedMeta.crumb)

    return drilledCategories.length > 0 ? drilledCategories[0].name.value : null
  }
}

export const getIsFirstLevelCategory = (breadcrumbsData: FhResponseData) => {
  const categoryCrumbs = breadcrumbsData.filter(
    (crumb: FhResponseData) =>
      !isBreadcrumbBannedCategory(crumb.name.non_ml) && crumb?.name?.attribute_type === selectors.CATEGORIES
  )

  return categoryCrumbs.length === 0
}

export const getInitialSelectedTab = (_queryUrlParams: string, path: string): Tab => {
  //If we have a urlParams query parameter in the url and it includes type_selector=<tab>
  if (_queryUrlParams?.includes(fhPatterns.TYPE_SELECTOR)) {
    const tabFromUrlParams: Tab = _queryUrlParams.substr(
      _queryUrlParams.indexOf(fhPatterns.TYPE_SELECTOR) + fhPatterns.TYPE_SELECTOR.length,
      searchDefaults.TABS_VALUE_LENGTH
    ) as Tab
    return tabFromUrlParams
  }

  //If we can't get the initial tab from query Params check if we are on a spare-parts url
  if (path?.includes(urlPatterns.SPARE_PARTS_DIRECTORY)) {
    return tabs.SPT
  }

  //Otherwise return the default tab 'PRO'
  return tabs.PRO
}

export const getTransformedImplicitRanges = (implicitRangesRaw: FhResponseData) => {
  const ignoredRanges = ranges.IGNORED_RANGES
  const validImplicitRanges = implicitRangesRaw.filter((rng) => !ignoredRanges.includes(rng))
  let implicitRange: object = null
  if (validImplicitRanges.length) {
    implicitRange = validImplicitRanges.reduce((acc, rng) => {
      const key = rng.substring(
        rng.indexOf(ranges.ATTRIBUTE_ID_START_DELIMITER) + 1,
        rng.indexOf(ranges.ATTRIBUTE_ID_END_DELIMITER)
      )
      const value = rng.substring(
        rng.indexOf(ranges.ATTRIBUTE_VALUE_START_DELIMITER) + 1,
        rng.indexOf(ranges.ATTRIBUTE_VALUE_END_DELIMITER)
      )
      acc[key] = value.split(ranges.ATTRIBUTE_MULTIPLE_VALUE_SEPARATOR)
      return acc
    }, {})
  }
  return implicitRange
}

/* This is used on lister pages. When pressing the remove breadcrumb on CH2 level,
    the user should be returned to the CH1 system landing page on the systems as tiles navigation. */
export const getParentCategoryId = (categoryValue: string) => {
  /* non_ml category value comes from Fredhopper in format {catalog01_roc374547xch_ch1100000xch_ch2100001xch_ch3132761xch}
        Extract the second to last id to get the parent and re-format it to Categories API id format to get CH2_100001. */

  /* This part gives us "ch2100001" */
  const searchCategoryId = categoryValue?.split("_")?.at(-2)?.split("x")?.[0]

  /* Format it to "CH2_100001" */
  const categoryId = (searchCategoryId?.slice(0, 3) + "_" + searchCategoryId?.slice(3))?.toUpperCase()

  return categoryId
}
